'use client';

import { useEffect, useState } from 'react';

import { Link } from '@/core/i18n/navigation';
import { BrandLogo } from '@/shared/blocks/common/brand-logo';
import { BuiltWith } from '@/shared/blocks/common/built-with';
import { LocaleSelector } from '@/shared/blocks/common/locale-selector';
import { ThemeToggler } from '@/shared/blocks/common/theme-toggler';
import { SmartIcon } from '@/shared/blocks/common/smart-icon';
import { NavItem } from '@/shared/types/blocks/common';
import { Footer as FooterType } from '@/shared/types/blocks/landing';

export function Footer({ footer }: { footer: FooterType }) {
  const [currentYear, setCurrentYear] = useState<number | null>(null);

  useEffect(() => {
    setCurrentYear(new Date().getFullYear());
  }, []);

  return (
    <footer
      id={footer.id}
      className={`py-8 sm:py-8 ${footer.className || ''} overflow-x-hidden`}
      // overflow-x-hidden防止-footer-撑出水平滚动条
    >
      <div className="container space-y-8 overflow-x-hidden">
        <div className="grid min-w-0 gap-12 md:grid-cols-5">
          <div className="min-w-0 space-y-4 break-words md:col-span-2 md:space-y-6">
            {footer.brand ? <BrandLogo brand={footer.brand} /> : null}

            {footer.brand?.description ? (
              <p
                className="text-muted-foreground text-sm text-balance break-words"
                dangerouslySetInnerHTML={{ __html: footer.brand.description }}
              />
            ) : null}
          </div>

          <div className="col-span-3 grid min-w-0 gap-6 sm:grid-cols-3">
            {footer.nav?.items.map((item, idx) => (
              <div key={idx} className="min-w-0 space-y-4 text-sm break-words">
                <span className="block font-medium break-words">
                  {item.title}
                </span>

                <div className="flex min-w-0 flex-wrap gap-4 sm:flex-col">
                  {item.children?.map((subItem, iidx) => (
                    <Link
                      key={iidx}
                      href={subItem.url || ''}
                      target={subItem.target || ''}
                      className="text-muted-foreground hover:text-primary block break-words underline decoration-current underline-offset-4 duration-150"
                    >
                      <span className="break-words">{subItem.title || ''}</span>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex min-w-0 flex-wrap items-center gap-4 sm:gap-8">
          {footer.show_built_with !== false ? <BuiltWith /> : null}
          <div className="min-w-0 flex-1" />
          {footer.show_theme !== false ? <ThemeToggler type="toggle" /> : null}
          {footer.show_locale !== false ? (
            <LocaleSelector type="button" />
          ) : null}
        </div>

        <div
          aria-hidden
          className="h-px min-w-0 [background-image:linear-gradient(90deg,var(--color-foreground)_1px,transparent_1px)] bg-[length:6px_1px] bg-repeat-x opacity-25"
        />

        <div className="flex min-w-0 flex-wrap justify-between gap-8">
          {footer.copyright ? (
            <p className="text-muted-foreground text-sm text-balance break-words">
              © {currentYear || new Date().getFullYear()}{' '}
              {footer.brand?.title || 'Grid Maker'}.{' '}
              <span dangerouslySetInnerHTML={{ __html: footer.copyright }} />
            </p>
          ) : footer.brand ? (
            <p className="text-muted-foreground text-sm text-balance break-words">
              © {currentYear || new Date().getFullYear()}{' '}
              <a
                href={footer.brand.url || '/'}
                className="text-primary hover:text-primary/80 cursor-pointer underline decoration-current underline-offset-4"
              >
                {footer.brand.title}
              </a>
              , All rights reserved
            </p>
          ) : null}

          {/* TinyLaunch Badge in the center */}
          <div className="flex justify-center items-center gap-4 text-xs text-muted-foreground">
            <a href="https://indiehackerstacks.com/products/grid-maker" target="_blank" rel="noopener nofollow" className="underline decoration-current underline-offset-4 hover:text-primary transition-colors">Indie Hacker Stacks</a>
            <a href="https://www.f6s.com/100sites" target="_blank" rel="noopener nofollow" className="underline decoration-current underline-offset-4 hover:text-primary transition-colors">F6S</a>
          </div>

          {footer.agreement ? (
            <div className="flex min-w-0 flex-wrap items-center gap-4">
              {footer.agreement?.items.map((item: NavItem, index: number) => (
                <Link
                  key={index}
                  href={item.url || ''}
                  target={item.target || ''}
                  className="text-muted-foreground hover:text-primary block text-xs break-words underline decoration-current underline-offset-4 duration-150"
                >
                  {item.title || ''}
                </Link>
              ))}
            </div>
          ) : null}

          {footer.social ? (
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              {footer.social?.items.map((item: NavItem, index) => (
                <Link
                  key={index}
                  href={item.url || ''}
                  target={item.target || ''}
                  className="text-muted-foreground hover:text-primary bg-background block cursor-pointer rounded-full p-2 duration-150"
                  aria-label={item.title || 'Social media link'}
                >
                  {item.icon && (
                    <SmartIcon name={item.icon as string} size={20} />
                  )}
                </Link>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </footer>
  );
}
